/**
 * Client API public (no-auth) — pour la surface F2 publique `/v1/public/*`.
 *
 * Distinct de `api.ts` (qui tape `supabase.auth.getSession()` pour le JWT
 * broker) — ici on N'envoie PAS d'Authorization header, c'est l'API qui
 * gère explicitement les routes publiques no-auth + token HMAC en path.
 *
 * Anti-leak strict côté wire (cf. cadrage F2 §2 + 9.N.1.B/B2 backend) :
 *   - GET `/v1/public/intake/:token` retourne 200 ok:false unique pour
 *     TOUTE défaillance token-level. Le helper traite ce cas comme une
 *     « réponse normale » (pas une exception) — le caller dispatche selon
 *     `body.ok` et `body.data?.status`.
 *   - POST multipart : 201 happy / 200 ok:false token-level / 400
 *     INVALID_PAYLOAD / 415 FILE_REJECTED / 500 INTERNAL_ERROR / 429
 *     RATE_LIMITED. Le helper expose la shape brute au caller.
 */

// Base URL de l'API. Ordre de priorité (mini-fix dev-smoke ergonomics 9.N.2 post-commit) :
//   1. VITE_API_BASE_URL — nom canonique préféré (aligne avec NEXT_PUBLIC_API_BASE_URL etc.)
//   2. VITE_API_URL      — alias legacy, toujours accepté
//   3. "http://localhost:3001" — fallback dev par défaut
// Cf. docs/tests/chunk9n2-public-intake-frontend.md §Dev smoke ergonomics.
const API_URL =
  import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? "http://localhost:3001";

/**
 * Shape canonique du wrap success/error backend (parité 9.K.16 +
 * `apps/shared/src/errors/builders.ts`).
 */
export type PublicApiEnvelope<T> =
  | { readonly ok: true; readonly data: T }
  | {
      readonly ok: false;
      readonly error: {
        readonly code: string;
        readonly messages: { readonly en: string; readonly fr: string };
        readonly details?: unknown;
        readonly request_id?: string;
      };
    };

/**
 * Result du helper — distingue erreurs réseau (throw) des erreurs API
 * structurées (envelope renvoyé tel quel pour dispatch caller).
 */
export type PublicFetchResult<T> = {
  readonly status: number;
  readonly envelope: PublicApiEnvelope<T>;
  readonly retryAfter: number | null;
};

export class PublicNetworkError extends Error {
  constructor(
    message: string,
    public override readonly cause?: unknown,
  ) {
    super(message);
    this.name = "PublicNetworkError";
  }
}

function readRetryAfter(res: Response): number | null {
  const raw = res.headers.get("retry-after");
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * GET `/v1/public/...` — no auth, no body.
 *
 * Ne throw PAS sur les responses 4xx/5xx structurées (envelope ok:false).
 * Le caller dispatche selon `result.envelope.ok` + `result.status`.
 *
 * Throw uniquement sur :
 *   - Erreur réseau (offline, DNS, CORS catastrophique).
 *   - Réponse non-JSON inattendue (rare — backend devrait toujours
 *     retourner JSON sur ces routes).
 */
export async function publicGet<T>(path: string): Promise<PublicFetchResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: "GET",
      headers: { accept: "application/json" },
    });
  } catch (err) {
    throw new PublicNetworkError("Network error during public GET", err);
  }
  return parsePublicResponse<T>(res);
}

/**
 * POST `/v1/public/...` multipart/form-data — no auth.
 *
 * **Important** : NE PAS forcer `content-type` — laisser le browser le set
 * automatiquement avec le bon `boundary` quand `body` est `FormData`.
 *
 * Le caller construit le `FormData` (`fd.append("consent", JSON.stringify(...))`,
 * `fd.append("files", file)`, etc.) et nous le passe brut.
 */
export async function publicPostMultipart<T>(
  path: string,
  formData: FormData,
): Promise<PublicFetchResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: { accept: "application/json" },
      body: formData,
    });
  } catch (err) {
    throw new PublicNetworkError("Network error during public POST multipart", err);
  }
  return parsePublicResponse<T>(res);
}

/**
 * POST `/v1/public/...` application/json — no auth (9.U.5 magic-link versionné).
 *
 * Le backend dispatche sur `content-type: application/json` vers le handler
 * versionné (data + consent + submission_id). Anti-leak identique au multipart :
 * 201 happy / 200 ok:false unavailable / 400 invalid / 409 idempotency-conflict /
 * 429 rate-limited / 500 internal. Le caller dispatche sur le result brut.
 */
/**
 * PUT public — l'écriture d'UNE ligne de census (9.BK.1).
 *
 * Idempotent par construction : la ligne est adressée par un identifiant que le
 * client a lui-même choisi, donc un envoi rejoué met à jour au lieu de dupliquer.
 */
export async function publicPutJson<T>(path: string, body: unknown): Promise<PublicFetchResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: "PUT",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new PublicNetworkError("Network error during public PUT json", err);
  }
  return parsePublicResponse<T>(res);
}

/** DELETE public — retrait EN DOUCEUR d'une ligne saisie par erreur. */
export async function publicDelete<T>(path: string): Promise<PublicFetchResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: "DELETE",
      headers: { accept: "application/json" },
    });
  } catch (err) {
    throw new PublicNetworkError("Network error during public DELETE", err);
  }
  return parsePublicResponse<T>(res);
}

export async function publicPostJson<T>(
  path: string,
  body: unknown,
): Promise<PublicFetchResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new PublicNetworkError("Network error during public POST json", err);
  }
  return parsePublicResponse<T>(res);
}

async function parsePublicResponse<T>(res: Response): Promise<PublicFetchResult<T>> {
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new PublicNetworkError(
      `Public API returned non-JSON response (status ${res.status}, content-type ${contentType || "<absent>"})`,
    );
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch (err) {
    throw new PublicNetworkError("Public API returned invalid JSON", err);
  }

  // Sanity check de structure — accepte ok:true avec data ou ok:false avec error.
  if (typeof body !== "object" || body === null || !("ok" in (body as Record<string, unknown>))) {
    throw new PublicNetworkError(
      `Public API response shape invalid (status ${res.status}, body keys: ${
        body && typeof body === "object" ? Object.keys(body).join(",") : "<not-object>"
      })`,
    );
  }

  return {
    status: res.status,
    envelope: body as PublicApiEnvelope<T>,
    retryAfter: readRetryAfter(res),
  };
}
